import { useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { X, ArrowRight, Clock, Star } from 'lucide-react'
import { strings } from '@/strings'
import { normaliseProfileInput, isValidProfileIdentifier } from '@/lib/profileNavigation'
import { useGotoProfileHistory } from '@/hooks/useGotoProfileHistory'
import { useAuth } from '@/hooks/useAuth'
import { useFavoriteAccounts } from '@/hooks/useFavoriteAccounts'
import { useProfileBatch } from '@/hooks/useProfileBatch'
import { Avatar } from '@/components/Avatar'
import { lock, unlock } from '@/lib/bodyScrollLock'

interface GoToProfileSheetProps {
    isOpen: boolean
    onClose: () => void
    /** Called just before the sheet navigates to a profile (successful submit or recent entry tap).
     *  Callers can use this to distinguish a successful submit from a user cancel in onClose. */
    onNavigate?: () => void
    /** Pre-filled input value (e.g. from PWA share target). */
    initialValue?: string
}

/** Duration must match the CSS transition on the sheet panel (ms). */
const EXIT_DURATION_MS = 200

export function GoToProfileSheet({
    isOpen,
    onClose,
    onNavigate,
    initialValue = '',
}: GoToProfileSheetProps) {
    const navigate = useNavigate()
    const inputRef = useRef<HTMLInputElement>(null)
    const closeButtonRef = useRef<HTMLButtonElement>(null)
    const submitButtonRef = useRef<HTMLButtonElement>(null)
    const [value, setValue] = useState(initialValue)
    const [error, setError] = useState<string | null>(null)
    const { recentIdentifiers, addIdentifier } = useGotoProfileHistory()
    const { session } = useAuth()
    const { accounts: favoriteAccounts } = useFavoriteAccounts()
    const { data: favoriteProfiles } = useProfileBatch(favoriteAccounts.map(a => a.did))
    const showFavorites = !!session && favoriteAccounts.length > 0

    // Track whether we're playing the exit animation (sheet is closing but not yet unmounted)
    const [isVisible, setIsVisible] = useState(false)
    const [isAnimatingOut, setIsAnimatingOut] = useState(false)

    const errorId = useId()
    const titleId = useId()

    const s = strings.goToProfile

    // Manage mount/unmount with exit animation
    useEffect(() => {
        if (isOpen) {
            setIsAnimatingOut(false)
            setIsVisible(true)
        } else if (isVisible) {
            // Trigger exit animation, then unmount after transition completes
            setIsAnimatingOut(true)
            const id = setTimeout(() => {
                setIsVisible(false)
                setIsAnimatingOut(false)
            }, EXIT_DURATION_MS)
            return () => clearTimeout(id)
        }
    }, [isOpen]) // eslint-disable-line react-hooks/exhaustive-deps

    // Sync initialValue changes (e.g. when opened via share target)
    useEffect(() => {
        setValue(initialValue)
    }, [initialValue, isOpen])

    // Autofocus the input when the sheet opens
    useEffect(() => {
        if (!isOpen) return
        // Small delay so the CSS animation has started and iOS doesn't fight
        // the keyboard with the bottom-sheet slide animation.
        const id = setTimeout(() => {
            inputRef.current?.focus()
        }, 80)
        return () => clearTimeout(id)
    }, [isOpen])

    // Close on Escape; trap Tab focus within the sheet
    useEffect(() => {
        if (!isOpen) return

        const getFocusable = (): HTMLElement[] => {
            // Ordered: close button → input → submit button
            const candidates: Array<HTMLElement | null> = [
                closeButtonRef.current,
                inputRef.current,
                submitButtonRef.current,
            ]
            return candidates.filter((el): el is HTMLElement => el !== null)
        }

        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose()
                return
            }

            if (e.key === 'Tab') {
                const focusable = getFocusable()
                if (focusable.length === 0) return

                const first = focusable[0]
                const last = focusable[focusable.length - 1]
                const active = document.activeElement as HTMLElement | null

                if (e.shiftKey) {
                    // Shift+Tab: wrap from first → last
                    if (active === first || !focusable.includes(active!)) {
                        e.preventDefault()
                        last.focus()
                    }
                } else {
                    // Tab: wrap from last → first
                    if (active === last || !focusable.includes(active!)) {
                        e.preventDefault()
                        first.focus()
                    }
                }
            }
        }

        document.addEventListener('keydown', onKeyDown)
        return () => document.removeEventListener('keydown', onKeyDown)
    }, [isOpen, onClose])

    // Prevent body scroll while sheet is open (reference-counted to avoid race with other overlays)
    useEffect(() => {
        if (!isOpen) return
        lock()
        return () => unlock()
    }, [isOpen])

    function handleSubmit(raw: string) {
        const normalised = normaliseProfileInput(raw)
        if (!normalised) {
            setError(s.errorEmpty)
            return
        }
        if (!isValidProfileIdentifier(normalised)) {
            setError(s.errorInvalid)
            return
        }
        setError(null)
        addIdentifier(normalised)
        onNavigate?.()
        onClose()
        void navigate(`/profile/${encodeURIComponent(normalised)}`)
    }

    function handleFormSubmit(e: React.FormEvent) {
        e.preventDefault()
        handleSubmit(value)
    }

    function handleRecentClick(identifier: string) {
        addIdentifier(identifier)
        onNavigate?.()
        onClose()
        void navigate(`/profile/${encodeURIComponent(identifier)}`)
    }

    function handleFavoriteClick(did: string) {
        addIdentifier(did)
        onNavigate?.()
        onClose()
        void navigate(`/profile/${encodeURIComponent(did)}`)
    }

    if (!isVisible) return null

    return createPortal(
        <div className="fixed inset-0 z-50 flex flex-col justify-end">
            {/* Backdrop */}
            <div
                data-testid="goto-profile-backdrop"
                className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                onClick={onClose}
                aria-hidden="true"
            />

            {/* Sheet */}
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                className={`relative w-full bg-surface-light dark:bg-surface-dark border-t border-border-light dark:border-border-dark rounded-t-2xl shadow-2xl pb-[env(safe-area-inset-bottom)] ${
                    isAnimatingOut
                        ? 'animate-out slide-out-to-bottom duration-200'
                        : 'animate-in slide-in-from-bottom duration-200'
                }`}
            >
                {/* Handle bar */}
                <div className="flex justify-center pt-3 pb-1">
                    <div className="w-10 h-1 rounded-full bg-border-light dark:bg-border-dark" />
                </div>

                <div className="px-4 pt-2 pb-4">
                    {/* Header row */}
                    <div className="flex items-center justify-between mb-4">
                        <h2
                            id={titleId}
                            className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark"
                        >
                            {s.sheetTitle}
                        </h2>
                        <button
                            ref={closeButtonRef}
                            onClick={onClose}
                            aria-label={s.closeAriaLabel}
                            className="p-1.5 rounded-full hover:bg-border-light/30 dark:hover:bg-white/10 text-text-secondary-light dark:text-text-secondary-dark transition-colors"
                        >
                            <X className="w-5 h-5" aria-hidden="true" />
                        </button>
                    </div>

                    {/* Input form */}
                    <form onSubmit={handleFormSubmit} noValidate>
                        <div className="flex gap-2">
                            <input
                                ref={inputRef}
                                type="text"
                                inputMode="url"
                                autoComplete="off"
                                autoCorrect="off"
                                autoCapitalize="none"
                                spellCheck={false}
                                value={value}
                                onChange={e => {
                                    setValue(e.target.value)
                                    if (error) setError(null)
                                }}
                                placeholder={s.inputPlaceholder}
                                aria-label={s.inputAriaLabel}
                                aria-describedby={error ? errorId : undefined}
                                aria-invalid={!!error}
                                className={`flex-1 px-4 py-3 bg-background-light dark:bg-white/5 border rounded-xl text-text-primary-light dark:text-text-primary-dark text-base transition-all duration-200 focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary/20 ${
                                    error
                                        ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                                        : 'border-border-light dark:border-border-dark'
                                }`}
                            />
                            <button
                                ref={submitButtonRef}
                                type="submit"
                                aria-label={s.submitButton}
                                className="flex items-center justify-center px-4 h-[50px] rounded-xl bg-zinc-800 dark:bg-zinc-700 text-white font-medium transition-colors active:scale-95 shrink-0"
                            >
                                <ArrowRight className="w-5 h-5" aria-hidden="true" />
                            </button>
                        </div>
                        {error && (
                            <p id={errorId} role="alert" className="mt-2 text-sm text-red-500">
                                {error}
                            </p>
                        )}
                    </form>

                    {/* Favorite profiles — AT Protocol authenticated users only */}
                    {showFavorites && (
                        <div className="mt-4">
                            <p className="text-xs font-medium text-text-secondary-light dark:text-text-secondary-dark uppercase tracking-wider mb-2">
                                {s.favoritesHeading}
                            </p>
                            <ul className="space-y-1 max-h-[220px] overflow-y-auto">
                                {favoriteAccounts.map(account => {
                                    const profile = favoriteProfiles?.get(account.did)
                                    const displayName =
                                        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty displayName/handle should fall through, not render blank
                                        profile?.displayName || profile?.handle || account.did
                                    return (
                                        <li key={account.did}>
                                            <button
                                                onClick={() => handleFavoriteClick(account.did)}
                                                aria-label={s.favoriteEntryAriaLabel(displayName)}
                                                className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl hover:bg-border-light/30 dark:hover:bg-white/10 transition-colors text-left"
                                            >
                                                <Avatar
                                                    src={profile?.avatar}
                                                    alt={displayName}
                                                    size="sm"
                                                />
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium text-text-primary-light dark:text-text-primary-dark truncate">
                                                        {displayName}
                                                    </p>
                                                    {profile?.handle && (
                                                        <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark truncate">
                                                            @{profile.handle}
                                                        </p>
                                                    )}
                                                </div>
                                                <Star
                                                    className="w-3.5 h-3.5 shrink-0 text-text-secondary-light dark:text-text-secondary-dark"
                                                    aria-hidden="true"
                                                />
                                            </button>
                                        </li>
                                    )
                                })}
                            </ul>
                        </div>
                    )}

                    {/* Recent profiles */}
                    {recentIdentifiers.length > 0 && (
                        <div className="mt-4">
                            <p className="text-xs font-medium text-text-secondary-light dark:text-text-secondary-dark uppercase tracking-wider mb-2">
                                {s.recentHeading}
                            </p>
                            <ul className="space-y-1">
                                {recentIdentifiers.map(identifier => (
                                    <li key={identifier}>
                                        <button
                                            onClick={() => handleRecentClick(identifier)}
                                            aria-label={s.recentEntryAriaLabel(identifier)}
                                            className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl hover:bg-border-light/30 dark:hover:bg-white/10 transition-colors text-left"
                                        >
                                            <Clock
                                                className="w-4 h-4 shrink-0 text-text-secondary-light dark:text-text-secondary-dark"
                                                aria-hidden="true"
                                            />
                                            <span className="text-sm text-text-primary-light dark:text-text-primary-dark truncate">
                                                {identifier}
                                            </span>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </div>
        </div>,
        document.body
    )
}
